import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  Copy,
  Download,
  Eye,
  ExternalLink,
  FileImage,
  ImageUp,
  LayoutDashboard,
  Link as LinkIcon,
  Loader2,
  LogOut,
  RefreshCw,
  Settings,
  Shield,
  UploadCloud,
  UserRound,
  Upload,
  Trash2,
  IdCard,
} from 'lucide-react'
import { ShotSelectLogo } from '../components/ShotSelectLogo'
import {
  StudioApiError,
  clearStudioToken,
  createStudioLink,
  fetchStudioMe,
  fetchStudioProfile,
  fetchStudioLink,
  finalizeStudioLink,
  deleteStudioLink,
  listStudioLinks,
  logoutStudio,
  readStudioToken,
  revokeStudioLink,
  saveStudioProfile,
  requestStudioEmailCode,
  signInWithEmailCode,
  uploadStudioAvatar,
  uploadStudioVariant,
  uploadStudioLogo,
  type StudioLink,
  type StudioProfile,
  type StudioSession,
} from '../api/studio'
import type { DownloadMode } from '../types'

interface StudioPortalProps {
  onOpenClientLink?: (url: string) => void
}

type PortalStatus = 'checking' | 'signed-out' | 'ready' | 'error'

const EMPTY_PROFILE: StudioProfile = {
  type: 'studio-owner',
  displayName: '',
  studioName: '',
  email: '',
  phone: '',
  website: '',
  brandLogoUrl: '',
  brandColor: '#111827',
  timezone: 'UTC',
  dateFormat: 'month-day-year',
  defaultTtlDays: 30,
  defaultQuotaTargetMin: 10,
  defaultQuotaTargetMax: 50,
  defaultPicksEnabled: true,
  defaultDownloadEnabled: 'off',
  reminderCadenceDays: 14,
  instagramUrl: '',
  facebookUrl: '',
}

function profileReadiness(profile: StudioProfile) {
  const checks = [
    {
      label: 'Studio identity',
      done: Boolean(profile.studioName.trim() && profile.displayName.trim()),
    },
    { label: 'Client contact', done: Boolean(profile.email.trim() && profile.phone.trim()) },
    { label: 'Brand kit', done: Boolean(profile.brandColor.trim() && profile.brandLogoUrl.trim()) },
    { label: 'Locale defaults', done: Boolean(profile.timezone.trim() && profile.dateFormat) },
    {
      label: 'Review defaults',
      done:
        profile.defaultTtlDays > 0 &&
        profile.defaultQuotaTargetMin > 0 &&
        profile.defaultQuotaTargetMax >= profile.defaultQuotaTargetMin,
    },
  ]
  const completed = checks.filter((check) => check.done).length
  return {
    checks,
    completed,
    total: checks.length,
    percent: Math.round((completed / checks.length) * 100),
  }
}

type PreparedDownloadMode = Exclude<DownloadMode, 'original'>

function preparedDownloadMode(downloadMode: DownloadMode): PreparedDownloadMode {
  return downloadMode === 'original' ? 'hr' : downloadMode
}

function normalizeProfileDownloadMode(profile: StudioProfile): StudioProfile {
  const defaultDownloadEnabled = preparedDownloadMode(profile.defaultDownloadEnabled)
  return defaultDownloadEnabled === profile.defaultDownloadEnabled
    ? profile
    : { ...profile, defaultDownloadEnabled }
}

export function StudioPortal({ onOpenClientLink }: StudioPortalProps) {
  const [status, setStatus] = useState<PortalStatus>('checking')
  const [session, setSession] = useState<StudioSession | null>(null)
  const [profile, setProfile] = useState<StudioProfile | null>(null)
  const [links, setLinks] = useState<StudioLink[]>([])
  const [linkBusy, setLinkBusy] = useState<Record<string, string | null>>({})
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const setLinkAction = (linkId: string, action: string | null) => {
    setLinkBusy((current) => ({ ...current, [linkId]: action }))
  }

  const runLinkAction = async (linkId: string, action: string, task: () => Promise<void>) => {
    try {
      setLinkAction(linkId, action)
      await task()
    } finally {
      setLinkAction(linkId, null)
    }
  }

  const syncLinkUsage = async (linkId: string) => {
    await runLinkAction(linkId, 'sync', async () => {
      const details = await fetchStudioLink(linkId)
      setLinks((current) =>
        current.map((link) =>
          link.id === linkId
            ? {
                ...link,
                ...details,
                syncedAt: details.syncedAt ?? Date.now(),
                storageBytes: details.storageBytes,
                photoCount: details.photoCount ?? details.photos.length,
                voterCount: details.voterCount ?? details.voters.length,
              }
            : link
        )
      )
    })
  }

  const revokeLink = async (linkId: string) => {
    await runLinkAction(linkId, 'revoke', async () => {
      await revokeStudioLink(linkId)
      setLinks((current) =>
        current.map((link) =>
          link.id === linkId
            ? {
                ...link,
                status: 'revoked',
                syncedAt: Date.now(),
              }
            : link
        )
      )
    })
  }

  const deleteLink = async (linkId: string) => {
    await runLinkAction(linkId, 'delete', async () => {
      await deleteStudioLink(linkId)
      setLinks((current) => current.filter((link) => link.id !== linkId))
    })
  }

  const refresh = useCallback(async (options: { showLoading?: boolean } = {}) => {
    const token = readStudioToken()
    if (!token) {
      setStatus('signed-out')
      setSession(null)
      setProfile(null)
      setLinks([])
      return
    }
    if (options.showLoading !== false) {
      setStatus('checking')
    }
    setError(null)
    try {
      const [nextSession, nextProfile, nextLinks] = await Promise.all([
        fetchStudioMe(),
        fetchStudioProfile(),
        listStudioLinks(),
      ])
      setSession(nextSession)
      setProfile(nextProfile)
      setLinks(nextLinks)
      setStatus('ready')
    } catch (err) {
      if (err instanceof StudioApiError && err.status === 401) {
        clearStudioToken()
        setStatus('signed-out')
        return
      }
      setError(err instanceof Error ? err.message : 'Unable to load studio workspace')
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const handleSignedIn = useCallback(
    async (nextSession: StudioSession) => {
      setSession(nextSession)
      try {
        const [nextProfile, nextLinks] = await Promise.all([
          fetchStudioProfile(),
          listStudioLinks(),
        ])
        setProfile(nextProfile)
        setLinks(nextLinks)
        setStatus('ready')
        navigate('/dashboard', { replace: true })
      } catch {
        await refresh()
      }
    },
    [navigate, refresh]
  )

  const handleSignOut = useCallback(async () => {
    await logoutStudio()
    setSession(null)
    setProfile(null)
    setLinks([])
    setStatus('signed-out')
    navigate('/login', { replace: true })
  }, [navigate])

  if (status === 'checking') return <StudioLoading />
  if (status === 'signed-out') return <StudioLogin onSignedIn={handleSignedIn} />
  if (status === 'error') return <StudioError message={error} onRetry={refresh} />

  return (
    <StudioShell
      session={session}
      profile={profile ?? EMPTY_PROFILE}
      links={links}
      linkBusy={linkBusy}
      onSyncLink={syncLinkUsage}
      onRevokeLink={revokeLink}
      onDeleteLink={deleteLink}
      onRefresh={refresh}
      onSignOut={handleSignOut}
      onOpenClientLink={onOpenClientLink}
    />
  )
}

function StudioLoading() {
  return (
    <div className="studio-center">
      <Loader2 size={26} className="animate-spin text-[var(--text-secondary)]" />
      <div className="text-sm text-[var(--text-secondary)]">Opening studio workspace…</div>
    </div>
  )
}

function StudioError({ message, onRetry }: { message: string | null; onRetry: () => void }) {
  return (
    <div className="studio-center">
      <div className="studio-alert-icon">
        <AlertCircle size={24} />
      </div>
      <div className="max-w-sm text-center">
        <div className="text-lg font-semibold">Studio workspace could not load</div>
        <div className="mt-1 text-sm text-[var(--text-secondary)]">
          {message ?? 'Check your connection and try again.'}
        </div>
      </div>
      <button className="studio-button studio-button-primary" onClick={onRetry}>
        Try again
      </button>
    </div>
  )
}

function StudioLogin({ onSignedIn }: { onSignedIn: (session: StudioSession) => void }) {
  // ShotSelect sign-in: a six-digit code mailed to the studio address.
  // (Google sign-in was retired; this is the only studio sign-in.)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [stage, setStage] = useState<'email' | 'code'>('email')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const requestCode = async () => {
    if (loading) return
    setLoading(true)
    setError(null)
    try {
      await requestStudioEmailCode(email.trim())
      setStage('code')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the sign-in code.')
    } finally {
      setLoading(false)
    }
  }

  const verifyCode = async () => {
    if (loading) return
    setLoading(true)
    setError(null)
    try {
      const login = await signInWithEmailCode(email.trim(), code.trim())
      onSignedIn(login)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code did not work — try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="studio-login">
      <div className="studio-login-grid">
        <div className="studio-login-hero">
          <div className="studio-mark">
            <ShotSelectLogo className="h-5 w-5" />
          </div>
          <div className="studio-login-hero-text">
            <div className="studio-eyebrow">ShotSelect Studio</div>
            <h1 className="studio-login-title">Cull, proof, and track client decisions.</h1>
            <p className="studio-login-copy">
              Sign in to create links, upload up to 50 photos from the web, and keep studio profile,
              links, and picks synced across desktop, browser, and future iOS.
            </p>
          </div>
        </div>
        <div className="studio-login-card">
          <div className="studio-login-panel" data-testid="studio-signin">
            {stage === 'email' ? (
              <form
                className="flex flex-col gap-2"
                onSubmit={(e) => {
                  e.preventDefault()
                  void requestCode()
                }}
              >
                <div className="studio-field">
                  <label className="studio-field-label" htmlFor="studio-signin-email">
                    Sign in with ShotSelect
                  </label>
                  <input
                    id="studio-signin-email"
                    data-testid="studio-signin-email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@yourstudio.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  className="studio-button studio-button-primary"
                  data-testid="studio-signin-request"
                  disabled={loading || email.trim().length === 0}
                >
                  Email me a sign-in code
                </button>
              </form>
            ) : (
              <form
                className="flex flex-col gap-2"
                onSubmit={(e) => {
                  e.preventDefault()
                  void verifyCode()
                }}
              >
                <div className="studio-field">
                  <label className="studio-field-label" htmlFor="studio-signin-code">
                    Enter the 6-digit code sent to {email.trim()}
                  </label>
                  <input
                    id="studio-signin-code"
                    data-testid="studio-signin-code"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
                <button
                  type="submit"
                  className="studio-button studio-button-primary"
                  data-testid="studio-signin-verify"
                  disabled={loading || code.trim().length !== 6}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  className="studio-button"
                  onClick={() => {
                    setStage('email')
                    setCode('')
                    setError(null)
                  }}
                >
                  Use a different email
                </button>
              </form>
            )}
            {loading && (
              <div className="studio-inline-status">
                <Loader2 size={14} className="animate-spin" />
                {stage === 'email' ? 'Sending code…' : 'Signing in…'}
              </div>
            )}
            {error && <div className="studio-form-error">{error}</div>}
            <div className="studio-login-note">
              Client links still open without an account. Studio sign-in only protects link
              creation, uploads, profile, and tracking.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function StudioShell({
  session,
  profile,
  links,
  linkBusy,
  onSyncLink,
  onRevokeLink,
  onDeleteLink,
  onRefresh,
  onSignOut,
  onOpenClientLink,
}: {
  session: StudioSession | null
  profile: StudioProfile
  links: StudioLink[]
  linkBusy: Record<string, string | null>
  onSyncLink: (linkId: string) => Promise<void>
  onRevokeLink: (linkId: string) => Promise<void>
  onDeleteLink: (linkId: string) => Promise<void>
  onRefresh: (options?: { showLoading?: boolean }) => Promise<void>
  onSignOut: () => Promise<void>
  onOpenClientLink?: (url: string) => void
}) {
  const location = useLocation()
  const active = location.pathname
  const newPicks = 0

  return (
    <div className="studio-shell">
      <aside className="studio-rail">
        <Link to="/dashboard" className="studio-rail-brand" aria-label="ShotSelect Studio home">
          <ShotSelectLogo className="h-5 w-5" />
          <span>ShotSelect</span>
        </Link>
        <nav className="studio-nav" aria-label="Studio navigation">
          <RailLink
            to="/dashboard"
            active={active === '/dashboard' || active === '/'}
            icon={LayoutDashboard}
          >
            Dashboard
          </RailLink>
          <RailLink to="/links" active={active.startsWith('/links')} icon={LinkIcon}>
            Links
          </RailLink>
          <RailLink to="/upload" active={active.startsWith('/upload')} icon={UploadCloud}>
            Upload
          </RailLink>
        </nav>
        <div className="studio-rail-footer">
          <RailLink to="/account" active={active.startsWith('/account')} icon={UserRound}>
            Account
          </RailLink>
          <button className="studio-rail-user" onClick={() => void onSignOut()}>
            <span className="studio-avatar">
              <AvatarImage
                src={session?.user.avatarUrl}
                fallback={initials(profile.studioName || profile.displayName)}
              />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-[var(--text-primary)]">
                {profile.studioName || session?.activeStudio?.name || 'Studio'}
              </span>
              <span className="block truncate text-xs text-[var(--text-tertiary)]">
                {session?.user.email}
              </span>
            </span>
            <LogOut size={15} />
          </button>
        </div>
      </aside>

      <main className="studio-main">
        <Routes>
          <Route
            path="/"
            element={
              <DashboardPage
                session={session}
                profile={profile}
                links={links}
                newPicks={newPicks}
                onRefresh={onRefresh}
              />
            }
          />
          <Route
            path="/dashboard"
            element={
              <DashboardPage
                session={session}
                profile={profile}
                links={links}
                newPicks={newPicks}
                onRefresh={onRefresh}
              />
            }
          />
          <Route
            path="/links"
            element={
              <LinksPage
                links={links}
                onRefresh={onRefresh}
                onOpenClientLink={onOpenClientLink}
                linkBusy={linkBusy}
                onSyncLink={onSyncLink}
                onRevokeLink={onRevokeLink}
                onDeleteLink={onDeleteLink}
              />
            }
          />
          <Route
            path="/upload"
            element={
              <UploadPage profile={profile} onCreated={() => onRefresh({ showLoading: false })} />
            }
          />
          <Route
            path="/account"
            element={
              <AccountPage
                session={session}
                profile={profile}
                links={links}
                onSaved={onRefresh}
                onSignOut={onSignOut}
              />
            }
          />
        </Routes>
      </main>
    </div>
  )
}

function RailLink({
  to,
  active,
  icon: Icon,
  children,
}: {
  to: string
  active: boolean
  icon: typeof LayoutDashboard
  children: string
}) {
  return (
    <Link to={to} className={`studio-nav-link ${active ? 'is-active' : ''}`}>
      <Icon size={17} />
      <span>{children}</span>
    </Link>
  )
}

function DashboardPage({
  session,
  profile,
  links,
  newPicks,
  onRefresh,
}: {
  session: StudioSession | null
  profile: StudioProfile
  links: StudioLink[]
  newPicks: number
  onRefresh: () => Promise<void>
}) {
  const activeLinks = links.filter((link) => link.status === 'active').length
  const totalOpens = links.reduce((sum, link) => sum + (link.openedCount ?? 0), 0)
  const totalDownloads = links.reduce((sum, link) => sum + (link.downloadCount ?? 0), 0)
  const latest = links.slice(0, 4)

  return (
    <section className="studio-page">
      <PageHeader
        eyebrow="Workspace"
        title={profile.studioName || session?.activeStudio?.name || 'Studio'}
        copy={session?.user.email ?? 'Signed in'}
        action={
          <button
            className="studio-icon-button"
            onClick={() => void onRefresh()}
            aria-label="Refresh"
          >
            <Loader2 size={16} />
          </button>
        }
      />

      <div className="studio-metrics">
        <Metric icon={LinkIcon} label="Active links" value={activeLinks} />
        <Metric icon={BadgeCheck} label="New picks" value={newPicks} />
        <Metric icon={Eye} label="Client opens" value={totalOpens} />
        <Metric icon={Download} label="Downloads" value={totalDownloads} />
      </div>

      <div className="studio-two-column">
        <div className="studio-section">
          <div className="studio-section-header">
            <div>
              <h2>Recent links</h2>
              <p>Open, copy, and monitor links created from desktop or web.</p>
            </div>
            <Link to="/links" className="studio-text-link">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          {latest.length ? (
            <div className="studio-link-list compact">
              {latest.map((link) => (
                <LinkRow key={link.id} link={link} compact />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={LinkIcon}
              title="No links yet"
              copy="Create your first proofing or delivery link from Upload."
              action={
                <Link to="/upload" className="studio-button studio-button-primary">
                  Create link
                </Link>
              }
            />
          )}
        </div>

        <div className="studio-section">
          <div className="studio-section-header">
            <div>
              <h2>Profile readiness</h2>
              <p>Used on client galleries, download emails, and future iOS account screens.</p>
            </div>
          </div>
          <div className="studio-profile-summary">
            <span className="studio-avatar large">
              <AvatarImage
                src={session?.user.avatarUrl}
                fallback={initials(profile.studioName || profile.displayName)}
              />
            </span>
            <div>
              <div className="font-semibold text-[var(--text-primary)]">
                {profile.studioName || 'Studio name missing'}
              </div>
              <div className="text-sm text-[var(--text-secondary)]">
                {profile.type.replace('-', ' ')}
              </div>
              <div className="mt-3 grid gap-1 text-sm text-[var(--text-tertiary)]">
                <span>{profile.displayName || 'Contact name not set'}</span>
                <span>{profile.email || session?.user.email}</span>
                <span>{profile.phone || 'Phone not set'}</span>
              </div>
            </div>
          </div>
          <Link to="/account" className="studio-button studio-button-secondary w-full">
            Edit profile
          </Link>
        </div>
      </div>
    </section>
  )
}

function LinksPage({
  links,
  onRefresh,
  onOpenClientLink,
  linkBusy,
  onSyncLink,
  onRevokeLink,
  onDeleteLink,
}: {
  links: StudioLink[]
  onRefresh: () => Promise<void>
  onOpenClientLink?: (url: string) => void
  linkBusy: Record<string, string | null>
  onSyncLink: (linkId: string) => Promise<void>
  onRevokeLink: (linkId: string) => Promise<void>
  onDeleteLink: (linkId: string) => Promise<void>
}) {
  return (
    <section className="studio-page">
      <PageHeader
        eyebrow="Account"
        title="Shared links"
        copy="Every cloud link returned by the server, with review and delivery state."
        action={
          <button
            className="studio-button studio-button-secondary"
            onClick={() => void onRefresh()}
          >
            Refresh
          </button>
        }
      />
      {links.length ? (
        <div className="studio-link-list">
          {links.map((link) => (
            <LinkRow
              key={link.id}
              link={link}
              onOpenClientLink={onOpenClientLink}
              busyAction={linkBusy[link.id] ?? null}
              onSync={onSyncLink}
              onRevoke={onRevokeLink}
              onDelete={onDeleteLink}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={LinkIcon}
          title="No shared links"
          copy="Upload up to 50 photos and create a review or delivery link."
          action={
            <Link to="/upload" className="studio-button studio-button-primary">
              Create link
            </Link>
          }
        />
      )}
    </section>
  )
}

function LinkRow({
  link,
  compact = false,
  onOpenClientLink,
  busyAction = null,
  onSync = async () => {},
  onRevoke = async () => {},
  onDelete = async () => {},
}: {
  link: StudioLink
  compact?: boolean
  onOpenClientLink?: (url: string) => void
  busyAction?: string | null
  onSync?: (linkId: string) => Promise<void>
  onRevoke?: (linkId: string) => Promise<void>
  onDelete?: (linkId: string) => Promise<void>
}) {
  const isBusy = Boolean(busyAction)
  const isRevoked = link.status === 'revoked'
  const usageParts = [
    link.photoCount !== undefined
      ? `${link.photoCount} photo${link.photoCount === 1 ? '' : 's'}`
      : null,
    link.voterCount !== undefined
      ? `${link.voterCount} voter${link.voterCount === 1 ? '' : 's'}`
      : null,
    link.openedCount !== undefined
      ? `${link.openedCount} open${link.openedCount === 1 ? '' : 's'}`
      : null,
    link.downloadCount !== undefined
      ? `${link.downloadCount} download${link.downloadCount === 1 ? '' : 's'}`
      : null,
    link.storageBytes !== undefined ? formatBytes(link.storageBytes) : null,
    link.lastClientEventAt ? `Last client ${formatRelative(link.lastClientEventAt)}` : null,
    link.syncedAt ? `Synced ${formatRelative(link.syncedAt)}` : null,
  ].filter(Boolean)

  const copy = async () => {
    await navigator.clipboard?.writeText(link.shareUrl)
  }
  const open = () => {
    if (onOpenClientLink) {
      onOpenClientLink(link.shareUrl)
      return
    }
    window.open(link.shareUrl, '_blank', 'noopener,noreferrer')
  }
  return (
    <article className="studio-link-row">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3>{link.eventTitle}</h3>
          <span className="studio-status">{link.status}</span>
        </div>
        <div className="studio-link-meta">
          <span>{link.clientName || 'Client not named'}</span>
          <span>{formatDate(link.createdAt)}</span>
          <span>{link.picksEnabled ? 'Picks on' : 'Picks off'}</span>
          <span>{downloadLabel(link.downloadEnabled)}</span>
        </div>
        {!compact && (
          <div className="studio-token-line">
            Token {link.shareToken.slice(0, 6)}…{link.shareToken.slice(-4)} · Expires{' '}
            {formatDate(link.expiresAt)}
          </div>
        )}
        {usageParts.length > 0 && (
          <div className="studio-link-meta">
            {usageParts.map((value) => (
              <span key={String(value)}>{value}</span>
            ))}
          </div>
        )}
      </div>
      <div className="studio-row-actions">
        <button
          className="studio-button studio-button-secondary compact"
          onClick={() => void open()}
          disabled={isRevoked || isBusy}
          data-testid={`studio-link-open-${link.id}`}
        >
          <ExternalLink size={13} />
          Open
        </button>
        <button
          className="studio-button studio-button-secondary compact"
          onClick={() => void copy()}
          disabled={isRevoked || isBusy}
          data-testid={`studio-link-copy-${link.id}`}
        >
          <Copy size={13} />
          Copy
        </button>
        {!compact && (
          <>
            <button
              className="studio-icon-button compact"
              onClick={() => void onSync(link.id)}
              disabled={isBusy}
              title="Sync usage"
              data-testid={`studio-link-sync-${link.id}`}
            >
              <RefreshCw
                size={13}
                className={isBusy && busyAction === 'sync' ? 'animate-spin' : ''}
              />
            </button>
            <button
              className="studio-icon-button compact danger"
              onClick={() => void onRevoke(link.id)}
              disabled={isRevoked || isBusy}
              title="Revoke link"
              data-testid={`studio-link-revoke-${link.id}`}
            >
              <Shield size={13} />
            </button>
            <button
              className="studio-icon-button compact danger"
              onClick={() => void onDelete(link.id)}
              disabled={isBusy}
              title="Delete link"
              data-testid={`studio-link-delete-${link.id}`}
            >
              <Trash2 size={13} />
            </button>
          </>
        )}
      </div>
      {!compact && link.clientEvents?.length ? (
        <div className="studio-link-events">
          <div className="studio-link-events-title">Recent client activity</div>
          {link.clientEvents.slice(0, 4).map((event) => (
            <div key={event.id} className="studio-link-event-row">
              <span>{formatClientEvent(event.eventType, event.photoId)}</span>
              <span>{formatRelative(event.ts)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </article>
  )
}

function UploadPage({
  profile,
  onCreated,
}: {
  profile: StudioProfile
  onCreated: () => Promise<void>
}) {
  const [files, setFiles] = useState<File[]>([])
  const [eventTitle, setEventTitle] = useState('')
  const [clientName, setClientName] = useState('')
  const [mode, setMode] = useState<'review' | 'delivery' | 'both'>(
    profile.defaultPicksEnabled && profile.defaultDownloadEnabled !== 'off'
      ? 'both'
      : profile.defaultPicksEnabled
        ? 'review'
        : 'delivery'
  )
  const [downloadEnabled, setDownloadEnabled] = useState<PreparedDownloadMode>(
    preparedDownloadMode(profile.defaultDownloadEnabled)
  )
  const [quality, setQuality] = useState<'standard' | 'high'>('standard')
  const [progress, setProgress] = useState('')
  const [createdUrl, setCreatedUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const addFiles = (next: FileList | File[]) => {
    const imageFiles = Array.from(next).filter((file) => file.type.startsWith('image/'))
    setFiles((current) => {
      const merged = [...current, ...imageFiles]
      if (merged.length > 50) {
        setError('Web upload is limited to 50 photos per link.')
      }
      return merged.slice(0, 50)
    })
  }

  const canCreate = files.length > 0 && eventTitle.trim().length > 0 && !busy

  const create = async () => {
    if (!canCreate) return
    setBusy(true)
    setError(null)
    setCreatedUrl(null)
    const photoIds = files.map((file, index) => ({
      file,
      photoId: `web_${typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}_${index}`}`,
    }))
    try {
      setProgress('Creating link…')
      const link = await createStudioLink({
        eventTitle: eventTitle.trim(),
        clientName: clientName.trim() || undefined,
        ttlDays: profile.defaultTtlDays,
        picksEnabled: mode !== 'delivery',
        downloadEnabled:
          mode === 'review' ? 'off' : downloadEnabled === 'off' ? 'web' : downloadEnabled,
        quotaTargetMin: profile.defaultQuotaTargetMin,
        quotaTargetMax: Math.max(
          profile.defaultQuotaTargetMin,
          Math.min(profile.defaultQuotaTargetMax, files.length)
        ),
        idempotencyKey: `web-${Date.now()}-${files.length}`,
        photos: photoIds.map(({ photoId }, index) => ({
          photoId,
          position: index,
          category: 'web upload',
          isRecommended: index < Math.min(8, files.length),
        })),
      })

      let done = 0
      const total = photoIds.length * 3
      for (const { file, photoId } of photoIds) {
        const variants = await renderVariants(file, quality)
        for (const variant of variants) {
          setProgress(`Uploading ${file.name} · ${done + 1}/${total}`)
          await uploadStudioVariant(link.linkId, photoId, variant.name, variant.blob)
          done += 1
        }
      }
      setProgress('Finalizing link…')
      await finalizeStudioLink(link.linkId)
      setCreatedUrl(link.shareUrl)
      setProgress('Link ready')
      await onCreated()
    } catch (err) {
      setError(formatStudioError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="studio-page">
      <PageHeader
        eyebrow="Create"
        title="Upload and share"
        copy="Create a real cloud link from browser photos. V1 web upload is capped at 50 photos."
      />

      <div className="studio-upload-grid">
        <div
          className="studio-dropzone"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault()
            addFiles(event.dataTransfer.files)
          }}
        >
          <ImageUp size={28} />
          <h2>Drop photos here</h2>
          <p>{files.length}/50 selected · JPEG, PNG, and browser-supported image files</p>
          <label className="studio-button studio-button-primary">
            Choose photos
            <input
              type="file"
              accept="image/*"
              multiple
              className="sr-only"
              onChange={(event) => event.currentTarget.files && addFiles(event.currentTarget.files)}
            />
          </label>
        </div>

        <div className="studio-section">
          <h2>Link configuration</h2>
          <div className="studio-form">
            <Field label="Event name">
              <input value={eventTitle} onChange={(event) => setEventTitle(event.target.value)} />
            </Field>
            <Field label="Client">
              <input value={clientName} onChange={(event) => setClientName(event.target.value)} />
            </Field>
            <Field label="Review process">
              <div className="studio-segment">
                {(['review', 'delivery', 'both'] as const).map((item) => (
                  <button
                    key={item}
                    className={mode === item ? 'is-active' : ''}
                    onClick={() => setMode(item)}
                    type="button"
                  >
                    {item === 'review' ? 'Review' : item === 'delivery' ? 'Delivery' : 'Both'}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Download quality">
              <select
                value={downloadEnabled}
                onChange={(event) => setDownloadEnabled(event.target.value as PreparedDownloadMode)}
                disabled={mode === 'review'}
              >
                <option value="off">Downloads off</option>
                <option value="web">Web-size WebP</option>
                <option value="hr">Largest available WebP</option>
              </select>
            </Field>
            <Field label="Upload quality">
              <select
                value={quality}
                onChange={(event) => setQuality(event.target.value as 'standard' | 'high')}
              >
                <option value="standard">Standard preview</option>
                <option value="high">Higher clarity</option>
              </select>
            </Field>
          </div>
          <button
            className="studio-button studio-button-primary w-full mt-4"
            disabled={!canCreate}
            onClick={() => void create()}
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
            Create cloud link
          </button>
          {progress && <div className="studio-inline-status">{progress}</div>}
          {error && <div className="studio-form-error">{error}</div>}
          {createdUrl && (
            <div className="studio-success">
              <CheckCircle2 size={16} />
              <a href={createdUrl} target="_blank" rel="noreferrer">
                {createdUrl}
              </a>
            </div>
          )}
        </div>
      </div>

      {files.length > 0 && (
        <div className="studio-file-list">
          {files.map((file) => (
            <div key={`${file.name}-${file.lastModified}-${file.size}`}>
              <FileImage size={15} />
              <span>{file.name}</span>
              <span>{Math.round(file.size / 1024)} KB</span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function AccountPage({
  session,
  profile,
  links,
  onSaved,
  onSignOut,
}: {
  session: StudioSession | null
  profile: StudioProfile
  links: StudioLink[]
  onSaved: () => Promise<void>
  onSignOut: () => Promise<void>
}) {
  const [draft, setDraft] = useState(() => normalizeProfileDownloadMode(profile))
  const [status, setStatus] = useState('')
  const [saving, setSaving] = useState(false)
  const [logoUploading, setLogoUploading] = useState(false)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState(session?.user.avatarUrl ?? '')
  const [showManualUrl, setShowManualUrl] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const avatarInputRef = useRef<HTMLInputElement>(null)
  const readiness = profileReadiness(draft)

  const triggerLogoUpload = () => {
    fileInputRef.current?.click()
  }

  const triggerAvatarUpload = () => {
    avatarInputRef.current?.click()
  }

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > 1024 * 1024) {
      setStatus('Profile icon is too large. Please select an image under 1MB.')
      return
    }
    try {
      setAvatarUploading(true)
      setStatus('')
      const res = await uploadStudioAvatar(file)
      if (res?.avatarUrl) {
        setAvatarUrl(res.avatarUrl)
        setStatus('Profile icon uploaded successfully')
        await onSaved()
      }
    } catch (err) {
      setStatus(formatStudioError(err))
    } finally {
      setAvatarUploading(false)
    }
  }

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      setStatus('Logo file is too large. Please select an image under 2MB.')
      return
    }
    try {
      setLogoUploading(true)
      setStatus('')
      const res = await uploadStudioLogo(file)
      if (res?.brandLogoUrl) {
        setDraft((current) => ({ ...current, brandLogoUrl: res.brandLogoUrl }))
        setStatus('Logo uploaded successfully')
        await onSaved()
      }
    } catch (err) {
      setStatus(formatStudioError(err))
    } finally {
      setLogoUploading(false)
    }
  }

  useEffect(() => {
    setDraft(normalizeProfileDownloadMode(profile))
  }, [profile])

  useEffect(() => {
    setAvatarUrl(session?.user.avatarUrl ?? '')
  }, [session?.user.avatarUrl])

  const save = async () => {
    setSaving(true)
    setStatus('')
    try {
      const normalizedDraft = normalizeProfileDownloadMode(draft)
      await saveStudioProfile(normalizedDraft)
      setDraft(normalizedDraft)
      setStatus('Profile saved')
      await onSaved()
    } catch (err) {
      setStatus(formatStudioError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="studio-page">
      <PageHeader
        eyebrow="Account"
        title="Profile and security"
        copy="Server-backed studio details used across desktop, browser, and future iOS."
      />
      <div className="studio-two-column">
        <div className="studio-section">
          <h2>Profile setup</h2>
          <div className="studio-form">
            <Field label="Account type">
              <select
                value={draft.type}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    type: event.target.value as StudioProfile['type'],
                  }))
                }
              >
                <option value="individual">Individual photographer</option>
                <option value="studio-owner">Studio owner</option>
                <option value="team-member">Team member</option>
              </select>
            </Field>
            <Field label="Your name">
              <input
                value={draft.displayName}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, displayName: event.target.value }))
                }
              />
            </Field>
            <Field label="Studio name">
              <input
                value={draft.studioName}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, studioName: event.target.value }))
                }
              />
            </Field>
            <Field label="Email">
              <input
                value={draft.email}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, email: event.target.value }))
                }
              />
            </Field>
            <Field label="Phone">
              <input
                value={draft.phone}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, phone: event.target.value }))
                }
              />
            </Field>
            <Field label="Website">
              <input
                value={draft.website}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, website: event.target.value }))
                }
              />
            </Field>
            <h3 className="pt-2 text-sm font-semibold text-[var(--text-primary)]">
              Brand and client trust
            </h3>
            <div className="studio-field">
              <label className="studio-field-label">Profile icon</label>
              <div className="studio-brand-upload">
                <div className="studio-brand-preview logo">
                  <AvatarImage
                    src={avatarUrl}
                    fallback={initials(draft.displayName || draft.studioName)}
                  />
                </div>
                <div className="studio-brand-actions">
                  <button
                    type="button"
                    onClick={triggerAvatarUpload}
                    disabled={avatarUploading}
                    className="studio-button studio-button-secondary compact"
                  >
                    {avatarUploading ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Upload size={14} />
                    )}
                    {avatarUrl ? 'Change icon' : 'Upload icon'}
                  </button>
                  <span className="studio-upload-hint">
                    Square PNG, JPG, or WebP. Max 1MB. Used inside the signed-in workspace.
                  </span>
                </div>
              </div>
              <input
                type="file"
                ref={avatarInputRef}
                className="hidden"
                accept="image/png, image/jpeg, image/webp"
                onChange={handleAvatarFileChange}
              />
            </div>
            <div className="studio-field">
              <label className="studio-field-label">Brand Logo</label>
              <div className="studio-brand-upload">
                <div className="studio-brand-preview">
                  {draft.brandLogoUrl ? (
                    <img
                      src={draft.brandLogoUrl}
                      className="w-full h-full object-contain"
                      alt="Brand logo preview"
                    />
                  ) : (
                    <IdCard className="h-6 w-6 opacity-30" />
                  )}
                </div>
                <div className="studio-brand-actions">
                  <div className="studio-brand-buttons">
                    <button
                      type="button"
                      onClick={triggerLogoUpload}
                      disabled={logoUploading}
                      className="studio-button studio-button-secondary compact"
                    >
                      {logoUploading ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload size={14} />
                          {draft.brandLogoUrl ? 'Change logo' : 'Upload logo'}
                        </>
                      )}
                    </button>
                    {draft.brandLogoUrl && (
                      <button
                        type="button"
                        onClick={() => setDraft((current) => ({ ...current, brandLogoUrl: '' }))}
                        className="studio-icon-button compact danger"
                        title="Remove logo"
                        aria-label="Remove logo"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <span className="studio-upload-hint">
                    Transparent PNG preferred. Max 2MB. Shown on client galleries and emails.
                  </span>
                </div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/png, image/jpeg, image/webp"
                onChange={handleLogoFileChange}
              />
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowManualUrl(!showManualUrl)}
                  className="text-[10px] text-[var(--text-tertiary)] underline hover:text-[var(--text-primary)]"
                >
                  {showManualUrl ? 'Hide manual URL' : 'Edit URL manually'}
                </button>
                {showManualUrl && (
                  <label className="studio-field">
                    <span>Logo URL</span>
                    <input
                      value={draft.brandLogoUrl}
                      placeholder="https://…/logo.png"
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, brandLogoUrl: event.target.value }))
                      }
                    />
                  </label>
                )}
              </div>
            </div>
            <Field label="Brand color">
              <input
                type="color"
                value={draft.brandColor || '#111827'}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, brandColor: event.target.value }))
                }
              />
            </Field>
            <Field label="Instagram">
              <input
                value={draft.instagramUrl}
                placeholder="https://instagram.com/yourstudio"
                onChange={(event) =>
                  setDraft((current) => ({ ...current, instagramUrl: event.target.value }))
                }
              />
            </Field>
            <Field label="Facebook">
              <input
                value={draft.facebookUrl}
                placeholder="https://facebook.com/yourstudio"
                onChange={(event) =>
                  setDraft((current) => ({ ...current, facebookUrl: event.target.value }))
                }
              />
            </Field>
            <h3 className="pt-2 text-sm font-semibold text-[var(--text-primary)]">
              Workflow defaults
            </h3>
            <Field label="Timezone">
              <input
                value={draft.timezone}
                placeholder="America/New_York"
                onChange={(event) =>
                  setDraft((current) => ({ ...current, timezone: event.target.value }))
                }
              />
            </Field>
            <Field label="Date format">
              <select
                value={draft.dateFormat}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    dateFormat: event.target.value as StudioProfile['dateFormat'],
                  }))
                }
              >
                <option value="month-day-year">May 28, 2026</option>
                <option value="day-month-year">28 May 2026</option>
                <option value="iso">2026-05-28</option>
              </select>
            </Field>
            <Field label="Default expiry">
              <input
                type="number"
                min={1}
                max={90}
                value={draft.defaultTtlDays}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    defaultTtlDays: Number(event.target.value),
                  }))
                }
              />
            </Field>
            <Field label="Pick target">
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  min={1}
                  value={draft.defaultQuotaTargetMin}
                  aria-label="Minimum pick target"
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      defaultQuotaTargetMin: Number(event.target.value),
                    }))
                  }
                />
                <input
                  type="number"
                  min={1}
                  value={draft.defaultQuotaTargetMax}
                  aria-label="Maximum pick target"
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      defaultQuotaTargetMax: Number(event.target.value),
                    }))
                  }
                />
              </div>
            </Field>
            <Field label="Default review mode">
              <select
                value={draft.defaultPicksEnabled ? 'picks-on' : 'delivery-only'}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    defaultPicksEnabled: event.target.value === 'picks-on',
                    defaultDownloadEnabled:
                      event.target.value === 'delivery-only' &&
                      current.defaultDownloadEnabled === 'off'
                        ? 'web'
                        : current.defaultDownloadEnabled,
                  }))
                }
              >
                <option value="picks-on">Client picks enabled</option>
                <option value="delivery-only">Delivery only</option>
              </select>
            </Field>
            <Field label="Default downloads">
              <select
                value={preparedDownloadMode(draft.defaultDownloadEnabled)}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    defaultDownloadEnabled: event.target.value as DownloadMode,
                    defaultPicksEnabled:
                      event.target.value === 'off'
                        ? current.defaultPicksEnabled
                        : current.defaultPicksEnabled,
                  }))
                }
              >
                <option value="off">Downloads off</option>
                <option value="web">Web-size WebP</option>
                <option value="hr">Largest available WebP</option>
              </select>
            </Field>
            <Field label="Reminder cadence">
              <input
                type="number"
                min={1}
                max={90}
                value={draft.reminderCadenceDays}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    reminderCadenceDays: Number(event.target.value),
                  }))
                }
              />
            </Field>
          </div>
          <button
            className="studio-button studio-button-primary w-full"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            Save profile
          </button>
          {status && <div className="studio-inline-status">{status}</div>}
        </div>

        <div className="studio-section">
          <h2>Setup readiness</h2>
          <div className="studio-security-list">
            <div>
              <BadgeCheck size={17} />
              <span>{readiness.percent}% complete</span>
            </div>
            {readiness.checks.map((check) => (
              <div key={check.label}>
                <CheckCircle2 size={17} />
                <span>
                  {check.done ? 'Ready' : 'Missing'} · {check.label}
                </span>
              </div>
            ))}
          </div>

          <h2 className="mt-6">Security</h2>
          <div className="studio-security-list">
            <div>
              <Shield size={17} />
              <span>Signed in as {session?.user.email}</span>
            </div>
            <div>
              <Settings size={17} />
              <span>Studio role {session?.activeStudio?.role ?? 'owner'}</span>
            </div>
            <div>
              <LinkIcon size={17} />
              <span>{links.length} shared links in this workspace</span>
            </div>
          </div>
          <button
            className="studio-button studio-button-secondary w-full"
            onClick={() => void onSignOut()}
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>
    </section>
  )
}

function PageHeader({
  eyebrow,
  title,
  copy,
  action,
}: {
  eyebrow: string
  title: string
  copy: string
  action?: ReactNode
}) {
  return (
    <header className="studio-page-header">
      <div>
        <div className="studio-eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        <p>{copy}</p>
      </div>
      {action}
    </header>
  )
}

function Metric({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof LinkIcon
  label: string
  value: number
}) {
  return (
    <div className="studio-metric">
      <Icon size={16} />
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function EmptyState({
  icon: Icon,
  title,
  copy,
  action,
}: {
  icon: typeof LinkIcon
  title: string
  copy: string
  action: ReactNode
}) {
  return (
    <div className="studio-empty">
      <Icon size={22} />
      <h3>{title}</h3>
      <p>{copy}</p>
      {action}
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="studio-field">
      <span className="studio-field-label">{label}</span>
      {children}
    </label>
  )
}

function AvatarImage({ src, fallback }: { src?: string | null; fallback: string }) {
  if (src) {
    return <img src={src} alt="" />
  }
  return <>{fallback}</>
}

async function renderVariants(
  file: File,
  quality: 'standard' | 'high'
): Promise<Array<{ name: 'thumb' | 'display' | 'zoom'; blob: Blob }>> {
  const bitmap = await createImageBitmap(file)
  try {
    const displayMax = quality === 'high' ? 1920 : 1600
    const zoomMax = quality === 'high' ? 2560 : 2200
    return [
      { name: 'thumb', blob: await renderWebp(bitmap, 480, 0.72) },
      {
        name: 'display',
        blob: await renderWebp(bitmap, displayMax, quality === 'high' ? 0.84 : 0.78),
      },
      { name: 'zoom', blob: await renderWebp(bitmap, zoomMax, quality === 'high' ? 0.88 : 0.82) },
    ]
  } finally {
    bitmap.close()
  }
}

async function renderWebp(bitmap: ImageBitmap, maxEdge: number, quality: number): Promise<Blob> {
  const ratio = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height))
  const width = Math.max(1, Math.round(bitmap.width * ratio))
  const height = Math.max(1, Math.round(bitmap.height * ratio))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas rendering is not available.')
  ctx.drawImage(bitmap, 0, 0, width, height)
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Browser could not encode WebP.'))
        else resolve(blob)
      },
      'image/webp',
      quality
    )
  })
}

function initials(input: string): string {
  const parts = input.trim().split(/\s+/).filter(Boolean)
  return (parts[0]?.[0] ?? 'S') + (parts[1]?.[0] ?? '')
}

function formatDate(value: number): string {
  if (!value) return 'Not finalized'
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(
    new Date(value)
  )
}

function formatRelative(value: number): string {
  if (!value) return 'never'
  const diff = Date.now() - value
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour
  const week = 7 * day
  if (diff < minute) return 'just now'
  if (diff < hour) return `${Math.round(diff / minute)}m ago`
  if (diff < day) return `${Math.round(diff / hour)}h ago`
  if (diff < week) return `${Math.round(diff / day)}d ago`
  return `${Math.round(diff / week)}w ago`
}

function formatBytes(value: number): string {
  const abs = Math.abs(value)
  if (abs === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const divisor = 1024
  let remaining = abs
  let unitIndex = 0
  while (remaining >= divisor && unitIndex < units.length - 1) {
    remaining /= divisor
    unitIndex += 1
  }
  return `${remaining.toFixed(unitIndex > 1 ? 2 : 1)} ${units[unitIndex]}`
}

function downloadLabel(mode: DownloadMode): string {
  const preparedMode = preparedDownloadMode(mode)
  if (preparedMode === 'off') return 'Downloads off'
  if (preparedMode === 'web') return 'Web-size WebP'
  return 'Largest available WebP'
}

function formatClientEvent(eventType: string, photoId: string | null): string {
  const label =
    eventType === 'link_opened'
      ? 'Opened link'
      : eventType === 'gallery_viewed'
        ? 'Viewed gallery'
        : eventType === 'review_opened'
          ? 'Opened review'
          : eventType === 'photo_opened'
            ? 'Opened photo'
            : eventType === 'photo_downloaded'
              ? 'Downloaded photo'
              : eventType === 'share_sheet_opened'
                ? 'Opened share sheet'
                : eventType === 'picks_submitted'
                  ? 'Submitted picks'
                  : eventType.replace(/_/g, ' ')
  return photoId ? `${label} ${photoId}` : label
}

function formatStudioError(err: unknown): string {
  if (err instanceof StudioApiError) {
    if (err.code === 'quota_photos_per_link')
      return `Web upload is limited to ${err.limit ?? 50} photos.`
    if (err.code === 'quota_active_links')
      return `Active web links are limited to ${err.limit ?? 5}.`
    if (err.code === 'invalid_mode') return 'Turn on picks, downloads, or both.'
    return err.code.replace(/_/g, ' ')
  }
  return err instanceof Error ? err.message : 'Something went wrong.'
}
